import { type MutableRefObject, useEffect, useRef, useState } from 'react';
import { selectBoostForWorkshop } from '@redux/selectors/workshopSelectors';
import { useAppSelector } from '@redux/store';

import { safeCalculateMindedResources } from '@/common/lib/safeCalculateMindedResources';

interface useGoodsProductionProps {
  incommingStorage?: number;
  localStock?: number;
  rate?: number;
  plantId?: string;
}

const useGoodsProduction = ({
  incommingStorage,
  localStock,
  rate,
  plantId,
}: useGoodsProductionProps): {
  producted: number;
  productedRef: MutableRefObject<number>;
} => {
  const boosts = useAppSelector((state) =>
    selectBoostForWorkshop(state, plantId),
  );
  const timerId = useRef<NodeJS.Timeout | undefined>();
  const [productedBy, setProductedBy] = useState<number>(0);
  const productedRef = useRef<number>(0);

  useEffect(() => {
    setProductedBy(0);
    productedRef.current = 0;

    if (incommingStorage && rate) {
      clearInterval(timerId.current);

      const changeProductedMaterials = () => {
        const updatedAt = new Date();

        return (): void => {
          const plant = {
            updatedAt,
            productionRate: rate,
            boosts: boosts || [],
          };

          const producted = safeCalculateMindedResources(plant);
          setProductedBy(producted);
          productedRef.current = producted;
        };
      };
      changeProductedMaterials()();

      timerId.current = setInterval(changeProductedMaterials(), 1000);
    }

    return (): void => {
      clearInterval(timerId.current);
    };
  }, [incommingStorage, rate, localStock]);

  useEffect(() => {
    return (): void => clearInterval(timerId.current);
  }, []);

  useEffect(() => {
    if (incommingStorage !== undefined) {
      if (productedBy >= incommingStorage) {
        clearInterval(timerId.current);
      }
    }
  }, [productedBy, incommingStorage]);

  return { producted: productedBy, productedRef };
};

export { useGoodsProduction };
